<template>
  <div class="branch-select">
    <model-list-select
        :list="pickList"
        :value="selectedObj"
        option-value="id"
        :custom-text="optionText"
        :placeholder="placeholder"
        :is-disabled="disabled"
        @input="onPick"
    />
  </div>
</template>

<script>
import { ModelListSelect } from 'vue-search-select';
import { fetchBranchOptions } from '@/services/walletService';

/**
 * Searchable branch picker reusing the shipments-page ModelListSelect for a familiar, clearable UX.
 *
 * Loads the full active-branch set once (client-side search), sorts wallet-DISABLED (ປິດ) branches
 * first, and prepends an explicit "ທັງໝົດ" (all-branches) row so an operator can always return to the
 * unfiltered view after picking a branch — ModelListSelect has no clear button of its own.
 *
 * The parent keeps a PRIMITIVE v-model (`branch_id`; '' = all); this wrapper bridges the
 * object↔primitive impedance (ModelListSelect binds/emits the whole item object).
 * VueSearchSelect CSS is imported globally in main.js.
 */
const ALL_OPTION = { id: '', name: 'ທັງໝົດ', _all: true };

export default {
  name: 'BranchSelect',
  components: { ModelListSelect },
  props: {
    value: { type: [String, Number], default: '' },
    placeholder: { type: String, default: 'ເລືອກສາຂາ...' },
    disabled: { type: Boolean, default: false },
    // Endpoint cap is 2000; load the full active-branch set in one page for client-side search.
    perPage: { type: Number, default: 2000 },
    // true ⇒ show the "ທັງໝົດ" row (clearable back to all); false ⇒ branch mandatory (no all-row).
    allowAll: { type: Boolean, default: true },
  },
  data() {
    return { branches: [], loading: false };
  },
  computed: {
    // Wallet-ACTIVE branches first, wallet-DISABLED (ປິດ) last; A→Z by name within each group.
    // Flip the rank (return 0 for disabled) to put ປິດ branches first instead. New array — source
    // order intact.
    sortedBranches() {
      const rank = (o) => (this.isDisabledBranch(o) ? 1 : 0);
      return this.branches.slice().sort((a, b) => rank(a) - rank(b) || (a.name || '').localeCompare(b.name || ''));
    },
    pickList() {
      return this.allowAll ? [ALL_OPTION, ...this.sortedBranches] : this.sortedBranches;
    },
    // Resolve the primitive value to the matching branch object for ModelListSelect (null ⇒ placeholder).
    selectedObj() {
      if (this.value === '' || this.value === null || this.value === undefined) return null;
      return this.branches.find((o) => String(o.id) === String(this.value)) || null;
    },
  },
  watch: {
    value: { immediate: true, handler() { this.ensureSelected(); } },
  },
  created() {
    this.loadAll();
  },
  methods: {
    isDisabledBranch(o) {
      return !!o && (o.wallet_enabled === false || o.wallet_enabled === 0);
    },
    // Display text: "PREFIX - Name", with a " (ປິດ)" suffix for wallet-disabled branches.
    optionText(o) {
      if (!o) return '';
      if (o._all) return o.name;
      const base = (o.prefix ? o.prefix + ' - ' : '') + (o.name || '');
      return this.isDisabledBranch(o) ? `${base} (ປິດ)` : base;
    },
    async loadAll() {
      this.loading = true;
      try {
        this.branches = await fetchBranchOptions(this.$axios, { perPage: this.perPage });
        this.ensureSelected();
      } catch (e) {
        /* non-fatal: keep whatever we have; the picker is still usable */
      } finally {
        this.loading = false;
      }
    },
    // Deep-link/preselect safety: if a branch_id is set but isn't in the loaded list (e.g. a
    // status!=1 branch the bulk fetch omits), resolve just that row by id and merge it in so the
    // control shows a name instead of the placeholder.
    async ensureSelected() {
      const v = this.value;
      if (v === '' || v === null || v === undefined) return;
      if (this.branches.some((o) => String(o.id) === String(v))) return;
      try {
        const r = await fetchBranchOptions(this.$axios, { id: v });
        if (r && r[0] && !this.branches.some((o) => String(o.id) === String(r[0].id))) {
          this.branches = [...this.branches, r[0]];
        }
      } catch (e) { /* keep as-is; shows placeholder */ }
    },
    onPick(item) {
      // ModelListSelect emits the selected list item (object), or '' when cleared/emptied.
      const id = item && typeof item === 'object' ? item.id : item;
      this.$emit('input', id === undefined || id === null ? '' : id);
    },
  },
};
</script>

<!--
  NOT scoped: ModelSelect renders a Semantic-UI "ui fluid … dropdown" whose classes live in the
  global VueSearchSelect stylesheet. Everything here is namespaced under `.branch-select` so it can
  never affect other vue-search-select instances elsewhere in the app.
-->
<style>
/* The dropdown is already full width (`fluid`). Keep a long selected branch name on one line with an
   ellipsis so it never widens the filter row (the full text stays available in the option list). */
.branch-select .ui.dropdown > .text {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
